// Clinical Scoring and Diagnostic Evaluation Engine for Child Developmental Screening

/**
 * Question scoring maps defining the exact point value (0.0 to 1.0)
 * for each possible answer, correctly handling:
 * 1. Standard milestone questions (Yes = 1, With Help/Partially = 0.5, No = 0)
 * 2. Inverted medical questions (No = 1.0 healthy, Yes = 0.0 flagged)
 * 3. Categorical milestone scales (e.g. Small sentences = 1.0, Single words = 0.6)
 */
export const QUESTION_SCORING_MAP = {
  // Gross Motor
  gm1: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  gm2: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  gm3: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  gm4: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  gm5: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  gm6: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  gm7: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },

  // Fine Motor
  fm1: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  fm2: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  fm3: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  fm4: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  fm5: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },
  fm6: { Yes: 1.0, 'With Help': 0.5, 'Not Sure': 0.25, No: 0.0 },

  // Speech & Communication
  cm1: { Yes: 1.0, Partially: 0.5, 'Not Sure': 0.25, No: 0.0 },
  cm2: { 'Small sentences': 1.0, 'Single words': 0.6, 'Not yet speaking': 0.0 },
  cm3: { Yes: 1.0, Partially: 0.5, 'Not Sure': 0.25, No: 0.0 },
  cm4: { Yes: 1.0, Partially: 0.5, 'Not Sure': 0.25, No: 0.0 },
  cm5: { Yes: 1.0, Partially: 0.5, No: 0.0 },

  // Social & Emotional
  ss1: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  ss2: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  ss3: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  ss4: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  ss5: { 'Easily manages': 1.0, 'Sometimes difficult': 0.5, 'Very difficult': 0.0 },

  // Vision Screening
  vs1: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  vs2: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  vs3: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  vs4: { No: 1.0, Yes: 0.0 }, // INVERTED: "No abnormality" is healthy

  // Hearing Screening
  hs1: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  hs2: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  hs3: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  hs4: { Yes: 1.0, 'Not Sure': 0.25, No: 0.0 },
  hs5: { No: 1.0, Mild: 0.5, 'Not Sure': 0.3, Significant: 0.0 }, // INVERTED: "No delay" is healthy

  // Medical History (INVERTED: "No complications" is normal/healthy)
  mh1: { No: 1.0, Yes: 0.0 },
  mh3: { No: 1.0, Yes: 0.0 },
  mh5: { No: 1.0, Yes: 0.0 },
};

export const CLINICAL_DOMAINS = [
  {
    id: 'gross_motor',
    prefix: 'gm',
    name: 'Gross Motor Skills',
    description: 'Postural control, independent mobility, climbing, and bilateral coordination.',
    questionIds: ['gm1', 'gm2', 'gm3', 'gm4', 'gm5', 'gm6', 'gm7'],
    color: '#0284c7', // Sky blue
    icon: 'Activity'
  },
  {
    id: 'fine_motor',
    prefix: 'fm',
    name: 'Fine Motor & Coordination',
    description: 'Palmar and pincer grasp, tool usage, manipulation, and self-feeding.',
    questionIds: ['fm1', 'fm2', 'fm3', 'fm4', 'fm5', 'fm6'],
    color: '#7c3aed', // Purple
    icon: 'Hand'
  },
  {
    id: 'communication',
    prefix: 'cm',
    name: 'Speech & Communication',
    description: 'Receptive and expressive language, word combinations, and phonetic clarity.',
    questionIds: ['cm1', 'cm2', 'cm3', 'cm4', 'cm5'],
    color: '#059669', // Emerald
    icon: 'MessageSquare'
  },
  {
    id: 'social_emotional',
    prefix: 'ss',
    name: 'Social & Emotional Engagement',
    description: 'Shared gaze, affective reciprocation, peer interest, and separation ease.',
    questionIds: ['ss1', 'ss2', 'ss3', 'ss4', 'ss5'],
    color: '#f59e0b', // Amber
    icon: 'Users'
  },
  {
    id: 'sensory_medical',
    prefix: 'sm',
    name: 'Sensory, Vision & Hearing',
    description: 'Bilateral ocular tracking, auditory startle, name orientation, and otologic history.',
    questionIds: ['vs1', 'vs2', 'vs3', 'vs4', 'hs1', 'hs2', 'hs3', 'hs4', 'hs5'],
    color: '#ec4899', // Pink
    icon: 'Eye'
  },
  {
    id: 'medical_prenatal',
    prefix: 'mh',
    name: 'Medical & Prenatal Markers',
    description: 'Gestational factors, perinatal complications, and familial developmental predispositions.',
    questionIds: ['mh1', 'mh3', 'mh5'],
    color: '#64748b', // Slate
    icon: 'ShieldCheck'
  }
];

export const RED_FLAG_CRITERIA = [
  {
    id: 'rf_eye_contact',
    check: (ans) => ans.ss1 === 'No',
    title: 'Reduced Eye Contact & Social Smiling',
    severity: 'critical',
    recommendation: 'Formal Developmental Pediatric or ASD observational protocol recommended.'
  },
  {
    id: 'rf_name_response',
    check: (ans) => ans.hs4 === 'No',
    title: 'Inconsistent Response to Name',
    severity: 'critical',
    recommendation: 'Evaluate both hearing acuity (audiometry) and joint attention capabilities.'
  },
  {
    id: 'rf_sound_response',
    check: (ans) => ans.cm1 === 'No' || ans.hs1 === 'No',
    title: 'Diminished Sound Responsiveness',
    severity: 'critical',
    recommendation: 'Urgent pediatric audiologist consultation to rule out conductive/sensorineural hearing deficit.'
  },
  {
    id: 'rf_speech_delay',
    check: (ans) => ans.cm2 === 'Not yet speaking' || ans.hs5 === 'Significant',
    title: 'Severe Speech & Expressive Language Delay',
    severity: 'high',
    recommendation: 'Pediatric Speech-Language Pathologist (SLP) assessment indicated.'
  },
  {
    id: 'rf_independent_walk',
    check: (ans) => ans.gm5 === 'No',
    title: 'Delayed Independent Walking & Ambulation',
    severity: 'high',
    recommendation: 'Pediatric physical therapy evaluation for neuromuscular tone and gait support.'
  },
  {
    id: 'rf_vision_abnormality',
    check: (ans) => ans.vs4 === 'Yes' || ans.vs3 === 'No',
    title: 'Visual Tracking or Ocular Misalignment Concern',
    severity: 'high',
    recommendation: 'Pediatric ophthalmology evaluation for strabismus or refractive concerns.'
  },
  {
    id: 'rf_perinatal_trauma',
    check: (ans) => ans.mh1 === 'Yes' || ans.mh3 === 'Yes',
    title: 'History of Perinatal or Birth Complications',
    severity: 'moderate',
    recommendation: 'Correlate with pediatric clinical records and developmental baseline.'
  }
];

/**
 * Calculates a complete, clinical-grade evaluation of screening answers.
 */
export function evaluateScreeningAnswers(answers = {}, remarks = {}) {
  let earnedPoints = 0;
  let totalPossible = 0;
  const scoredQuestionIds = Object.keys(QUESTION_SCORING_MAP);

  scoredQuestionIds.forEach(qId => {
    totalPossible += 1.0;
    const answerVal = answers[qId];
    const scoreMap = QUESTION_SCORING_MAP[qId];

    if (answerVal !== undefined && scoreMap && scoreMap[answerVal] !== undefined) {
      earnedPoints += scoreMap[answerVal];
    } else if (answerVal !== undefined) {
      // Default fallback if unexpected value
      earnedPoints += 0.0;
    }
  });

  const percentage = totalPossible > 0 ? Math.round((earnedPoints / totalPossible) * 100) : 0;

  // Domain by Domain calculation
  const domainBreakdown = CLINICAL_DOMAINS.map(domain => {
    let domainEarned = 0;
    let domainPossible = 0;

    domain.questionIds.forEach(qId => {
      const scoreMap = QUESTION_SCORING_MAP[qId];
      if (scoreMap) {
        domainPossible += 1.0;
        const answerVal = answers[qId];
        if (answerVal !== undefined && scoreMap[answerVal] !== undefined) {
          domainEarned += scoreMap[answerVal];
        }
      }
    });

    const domainPercent = domainPossible > 0 ? Math.round((domainEarned / domainPossible) * 100) : 0;
    let status = 'On Track';
    let statusVariant = 'success';

    if (domainPercent < 60) {
      status = 'Needs Clinical Support';
      statusVariant = 'destructive';
    } else if (domainPercent < 80) {
      status = 'Monitor & Scaffold';
      statusVariant = 'warning';
    }

    return {
      id: domain.id,
      name: domain.name,
      description: domain.description,
      score: domainEarned,
      maxScore: domainPossible,
      percentage: domainPercent,
      status,
      statusVariant,
      color: domain.color
    };
  });

  // Check Red Flags
  const detectedRedFlags = RED_FLAG_CRITERIA
    .filter(crit => crit.check(answers))
    .map(crit => ({
      id: crit.id,
      title: crit.title,
      severity: crit.severity,
      recommendation: crit.recommendation
    }));

  // Overall Risk Classification
  let riskLevel = 'Low';
  let riskBadgeVariant = 'success';
  let riskDescription = 'Developmentally on track across standard pediatric milestones.';

  if (percentage < 60 || detectedRedFlags.some(r => r.severity === 'critical')) {
    riskLevel = 'Elevated';
    riskBadgeVariant = 'destructive';
    riskDescription = 'Multiple developmental markers or key clinical red flags indicate prompt clinical evaluation is recommended.';
  } else if (percentage < 80 || detectedRedFlags.length > 0) {
    riskLevel = 'Moderate';
    riskBadgeVariant = 'warning';
    riskDescription = 'Select milestone gaps identified. Recommended for structured monitoring and targeted early intervention.';
  }

  // Recommended Specialists based on deficits
  const recommendedSpecialists = [];
  if (answers.cm2 === 'Not yet speaking' || answers.hs5 === 'Significant' || answers.cm5 === 'No') {
    recommendedSpecialists.push({
      specialty: 'Speech & Language Pathologist (SLP)',
      reason: 'Support vocabulary expansion, phonetic clarity, and functional communication.'
    });
  }
  if (answers.gm5 === 'No' || answers.gm6 === 'No' || answers.fm3 === 'No') {
    recommendedSpecialists.push({
      specialty: 'Pediatric Physiotherapist / Occupational Therapist',
      reason: 'Address motor planning, muscle tone, and fine/gross motor milestone attainment.'
    });
  }
  if (answers.vs4 === 'Yes' || answers.vs3 === 'No') {
    recommendedSpecialists.push({
      specialty: 'Pediatric Ophthalmologist',
      reason: 'Assess ocular alignment and visual development.'
    });
  }
  if (answers.hs4 === 'No' || answers.cm1 === 'No') {
    recommendedSpecialists.push({
      specialty: 'Pediatric Audiologist / ENT',
      reason: 'Perform comprehensive audiometry to ensure hearing clarity.'
    });
  }
  if (recommendedSpecialists.length === 0 || riskLevel !== 'Low') {
    recommendedSpecialists.unshift({
      specialty: 'Developmental Pediatrician',
      reason: 'Comprehensive developmental assessment and routine milestone guidance.'
    });
  }

  // Qualitative parent concerns
  const parentConcerns = {
    general: answers.pc1 || remarks.pc1 || '',
    primary: answers.pc2 || remarks.pc2 || '',
    eyeIssue: answers.vs5 || '',
    pregnancyDesc: answers.mh2 || '',
    birthDesc: answers.mh4 || '',
    familyDesc: answers.mh6 || ''
  };

  return {
    score: Math.round(earnedPoints * 10) / 10,
    maxScore: totalPossible,
    percentage,
    riskLevel,
    riskBadgeVariant,
    riskDescription,
    domainBreakdown,
    redFlags: detectedRedFlags,
    recommendedSpecialists,
    parentConcerns,
    evaluatedDate: new Date().toISOString()
  };
}
